import * as React from "react"
import { Modal, Form, Button, Col } from "react-bootstrap"
import { concat, of, Subject, Subscription } from "rxjs"
import { filter, map, switchMap } from "rxjs/operators"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import Role from "../../api/Roles/Model/Role"
import { GetRoleDroplist } from "../../api/Roles/UserRolesGet"
import { EmailRegexp, PhoneRegexp } from "../../config/Constants"
import { EmailCheck } from "../../api/Invitation/InformationGet"
import MaskedInput from "react-maskedinput"
import ActionButton from "../ActionButton"
import { CreateSiteAdministrator } from "../../api/Invitation/InvitationCreate"
import { AppState, AssignedClinic } from "../../AppState"
import { RoleIdForSelectedCompany } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { APIError } from "../../api/ObservableFromFetch"

interface AdminSetupModalProps {
  show: boolean
  onClose: (created: boolean) => void
}

interface  AdminSetupModalAuthProps {
  PrimaryRoleID?: number
}

interface AdminSetupModalValidity {
  emailValid: boolean
  phoneValid: boolean
  roleValid: boolean
}

export interface AdminSetupModalData {
  email: string
  phone: string
  roleID?: number
}

interface AdminSetupModalState extends AdminSetupModalData, AdminSetupModalValidity {
  emailChecking: boolean
  emailAvailable?: boolean
  saving: boolean
  rolesLoading: boolean
  roles?: Role[]
}

export class AdminSetupModal extends React.PureComponent<AdminSetupModalProps & AdminSetupModalAuthProps, AdminSetupModalState> {
  emptyData: AdminSetupModalData = {
    email: "",
    phone: "",
    roleID: undefined,
  }
  positiveValidity: AdminSetupModalValidity = {
    emailValid: true,
    phoneValid: true,
    roleValid: true,
  }
  defaultState: AdminSetupModalState = {
    ...this.emptyData,
    ...this.positiveValidity,
    emailChecking: false,
    emailAvailable: undefined,
    rolesLoading: false,
    saving: false,
  }
  state: AdminSetupModalState = { ...this.defaultState }

  checkEmail$ = new Subject<string>()

  emailExistsSubscription?: Subscription
  userRolesSubscription?: Subscription
  saveSubscription?: Subscription

  componentDidMount() {
    this.emailExistsSubscription = this.checkEmail$.pipe(
      map(name => name.trim()),
      filter(name => this.isEmailValid(name)),
      switchMap(name => concat(
        of({ loading: true }),
        EmailCheck(name).pipe(
          map(result => ({ loading: false, result: result.IsEmailExists }))
        )
    ))
    ).subscribe(
      (o: { loading: boolean, result?: boolean }) => this.setState({ emailChecking: o.loading, emailAvailable: !o.result }),
      (error) => this.handleAPIError(error, { emailChecking: false, emailAvailable: undefined })
    )

    //TODO: replace with correct RoleID
    const roleID = this.props.PrimaryRoleID || 0
    this.setState({ rolesLoading: true })
    this.userRolesSubscription = GetRoleDroplist(roleID).subscribe(
      (data: Role[]) => this.setState({ roles: data, roleID: data.length === 1 ? data[0].RoleID : undefined, rolesLoading: false }),
      (error) => this.handleAPIError(error, { rolesLoading: false, emailAvailable: undefined })
    )
  }

  componentWillUnmount() {
    this.emailExistsSubscription && this.emailExistsSubscription.unsubscribe()
    this.userRolesSubscription && this.userRolesSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  isEmailValid = (value: string) => EmailRegexp.test(value)
  isPhoneValid = (value: string) => PhoneRegexp.test(value)
  isRoleValid = (value: number | undefined): boolean => !!value && !!this.state.roles?.find(item => item.RoleID === value)

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ email: value, emailValid: this.isEmailValid(value), emailAvailable: undefined })
  }

  handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ phone: value, phoneValid: this.isPhoneValid(value) })
  }

  handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value)
    this.setState({ roleID: value, roleValid: this.isRoleValid(value) })
  }

  handleClose = (hasChanges: boolean) => {
    const { roles } = this.state
    this.setState({ ...this.defaultState, roleID: roles && roles.length === 1 ? roles[0].RoleID : undefined })
    this.props.onClose(hasChanges)
  }

  handleCreateClick = () => {
    if (this.state.emailChecking || this.state.rolesLoading) {
      return
    }
    const { email, emailAvailable, phone, roleID } = this.state
    const validity: AdminSetupModalValidity = {
      emailValid: this.isEmailValid(email),
      phoneValid: this.isPhoneValid(phone),
      roleValid: this.isRoleValid(roleID)
    }
    const isValid = Object.values(validity).every((valid: boolean) => valid)
    if (!isValid) {
      this.setState(validity)
      return
    }
    if (!emailAvailable || emailAvailable === undefined) {
      return
    }

    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = CreateSiteAdministrator(email, phone.replace(/-/g, ""), window.location.hostname, roleID || 0).subscribe(
      (data) => {
        toast.success(data)
        this.handleClose(true)
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  render() {
    const { email, emailValid, emailChecking, emailAvailable, phone, phoneValid, roleID, roleValid, rolesLoading } = this.state

    return (<>
      <Modal centered backdrop="static" size="xl" show={this.props.show} onHide={this.props.onClose}>
        <Modal.Header closeButton>
          <Modal.Title>SITE ADMINISTRATOR SETUP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Row className="form-group required">
              <Form.Label column sm={3} htmlFor="email">Site Administrator Email Address</Form.Label>
              <Col sm={9}>
                <Form.Control id="email"
                  type="text"
                  value={email}
                  onChange={this.handleEmailChange}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => this.checkEmail$.next(e.target.value)}
                  isInvalid={!emailValid}
                  disabled={emailChecking} />
                {!emailChecking && !emailValid && <Form.Control.Feedback type="invalid" className="d-block">Please enter valid email</Form.Control.Feedback>}
                {!emailChecking && emailAvailable && < Form.Control.Feedback type="valid" className="d-block">Email is available</Form.Control.Feedback>}
                {!emailChecking && emailAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">Email is already in use</Form.Control.Feedback>}
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={3} htmlFor="phone">Site Administrator Personal Phone</Form.Label>
              <Col sm={9}>
                <Form.Control id="phone" type="text" as={MaskedInput} mask="111-111-1111" value={phone} onChange={this.handlePhoneChange} isInvalid={!phoneValid} />
                <Form.Text>This phone number will be used to validate the Administrator. Please provide a direct number or mobile number (not a main company number or extension)</Form.Text>
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={3} htmlFor="role">Role</Form.Label>
              <Col sm={9}>
                <Form.Control id="role" as="select" onChange={this.handleRoleChange} value={roleID || ""} isInvalid={!roleValid}>
                  {this.state.roles && this.state.roles.length > 1 && <option value="">Select Role</option>}
                  {this.state.roles?.map(role => <option value={role.RoleID} key={role.RoleID} title={role.Description}>{role.Title}</option>)}
                </Form.Control>
              </Col>
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.handleClose(false)}>Cancel</Button>
          <ActionButton variant="primary" disabled={emailChecking || rolesLoading} onClick={this.handleCreateClick} spin={this.state.saving} disableOnSpin>Create</ActionButton>
        </Modal.Footer>
      </Modal>
    </>
    )
  }
}

function mapStateToProps(state: AppState): AdminSetupModalAuthProps {
  return {
    PrimaryRoleID: RoleIdForSelectedCompany(state)
  }
}

const ConnectedAdminSetupModal = connect(mapStateToProps, null)(AdminSetupModal)
export default ConnectedAdminSetupModal
